import { useEffect, useEffectEvent, useRef, useState, useSyncExternalStore } from "react";

import { useIsDocumentVisible } from "@/hooks/useIsDocumentVisible";
import { cn } from "@/utils/cn";

import { Toast } from "./Toast";
import { toastStore } from "./toastApi";
import type { ToastEntry, ToastStore } from "./toastStore";

/** Сколько элементов видно одновременно; остальные ждут в очереди, их таймер не идёт. */
export const TOAST_MAX_VISIBLE = 5;

/** Длительность анимации исчезновения; совпадает с `duration-200` у элемента. */
const EXIT_DURATION_MS = 200;

interface ToastWrapperProps {
    store?: ToastStore;
    maxVisible?: number;
}

/**
 * Регион уведомлений в правом нижнем углу. Подключается один раз в `src/app/providers.tsx`;
 * элементы добавляются через `toast.*` из любого места.
 */
export function ToastWrapper({ store = toastStore, maxVisible = TOAST_MAX_VISIBLE }: ToastWrapperProps) {
    const entries = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);
    const isDocumentVisible = useIsDocumentVisible();

    return (
        <section
            aria-label="Уведомления"
            aria-live="polite"
            aria-relevant="additions text"
            // Регион — отдельный потомок body: Headless UI Dialog не делает его inert и не считает клик по нему
            // кликом «снаружи», поэтому уведомления можно закрыть поверх модалки, не закрывая её.
            className="pointer-events-none fixed inset-x-4 bottom-4 z-60 sm:left-auto sm:w-96"
        >
            <ol className="flex flex-col items-stretch gap-2">
                {entries.slice(0, maxVisible).map((entry) => (
                    <ToastWrapperItem key={entry.id} entry={entry} store={store} isTimerPaused={!isDocumentVisible} />
                ))}
            </ol>
        </section>
    );
}

interface ToastWrapperItemProps {
    entry: ToastEntry;
    store: ToastStore;
    isTimerPaused: boolean;
}

function ToastWrapperItem({ entry, store, isTimerPaused }: ToastWrapperItemProps) {
    const { id, content, duration, status, version } = entry;
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const isClosing = status === "closing";

    useAutoDismiss({
        duration,
        version,
        isPaused: isTimerPaused || isHovered || isFocused || isClosing,
        onExpire: () => store.dismiss(id),
    });

    useEffect(() => {
        if (!isClosing) return;
        const timer = setTimeout(() => store.remove(id), EXIT_DURATION_MS);
        return () => clearTimeout(timer);
    }, [isClosing, id, store]);

    const dismiss = () => store.dismiss(id);

    return (
        <li
            data-state={status}
            className={cn(
                "pointer-events-auto transition-all duration-200 ease-out motion-reduce:transition-none",
                "starting:translate-x-8 starting:opacity-0",
                isClosing ? "translate-x-8 opacity-0" : "translate-x-0 opacity-100",
            )}
            onPointerEnter={(event) => event.pointerType === "mouse" && setIsHovered(true)}
            onPointerLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false);
            }}
        >
            {content.type === "message" ? (
                <Toast variant={content.variant} title={content.title} onClose={dismiss}>
                    {content.message}
                </Toast>
            ) : (
                content.render({ id, dismiss })
            )}
        </li>
    );
}

interface UseAutoDismissOptions {
    duration: number | null;
    /** Новое значение начинает отсчёт заново (элемент обновили). */
    version: number;
    isPaused: boolean;
    onExpire: () => void;
}

/** Таймер автозакрытия с паузой: при паузе запоминает остаток и продолжает с него. */
function useAutoDismiss({ duration, version, isPaused, onExpire }: UseAutoDismissOptions) {
    const remainingRef = useRef(duration);
    const expire = useEffectEvent(onExpire);

    useEffect(() => {
        remainingRef.current = duration;
    }, [duration, version]);

    useEffect(() => {
        const remaining = remainingRef.current;
        if (remaining === null || isPaused) return;

        const startedAt = Date.now();
        const timer = setTimeout(() => expire(), Math.max(remaining, 0));
        return () => {
            clearTimeout(timer);
            remainingRef.current = remaining - (Date.now() - startedAt);
        };
    }, [duration, version, isPaused]);
}
